import React, { useEffect, useState } from 'react';
import { AutoLogout } from '@backstage/core-components';
import { useApi, configApiRef, identityApiRef } from '@backstage/core-plugin-api';

/**
 * AutoLogoutWrapper component that provides role-based auto-logout functionality.
 *
 * This component wraps Backstage's AutoLogout component and applies different
 * idle timeout durations based on user group membership. Admin users get a
 * shorter timeout for enhanced security.
 *
 * Configuration example:
 * ```yaml
 * auth:
 *   autologout:
 *     enabled: true
 *     defaultIdleTimeoutMinutes: 60      # Regular users
 *     adminIdleTimeoutMinutes: 30        # Admin users
 *     promptBeforeIdleSeconds: 60        # Warning before logout
 *     useWorkerTimers: true
 *     logoutIfDisconnected: true
 *     adminGroups:
 *       - group:default/backstage_system_admin
 *       - group:default/backstage_rbac_admin
 * ```
 */
export const AutoLogoutWrapper: React.FC = () => {
  const configApi = useApi(configApiRef);
  const identityApi = useApi(identityApiRef);

  const [idleTimeoutMinutes, setIdleTimeoutMinutes] = useState<number | undefined>(undefined);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    const determineTimeout = async () => {
      try {
        const enabled = configApi.getOptionalBoolean('auth.autologout.enabled') ?? true;

        if (!enabled) {
          setIsLoading(false);
          return;
        }

        const defaultTimeout = configApi.getOptionalNumber('auth.autologout.defaultIdleTimeoutMinutes') ?? 60;
        const adminTimeout = configApi.getOptionalNumber('auth.autologout.adminIdleTimeoutMinutes') ?? 30;
        const adminGroups = configApi.getOptionalStringArray('auth.autologout.adminGroups') ?? [
          'group:default/backstage_system_admin',
        ];

        const identity = await identityApi.getBackstageIdentity();
        const ownershipEntityRefs = identity.ownershipEntityRefs || [];

        const isAdmin = ownershipEntityRefs.some(ref =>
          adminGroups.some(adminGroup =>
            ref.toLowerCase() === adminGroup.toLowerCase()
          )
        );

        const selectedTimeout = isAdmin ? adminTimeout : defaultTimeout;

        setIdleTimeoutMinutes(selectedTimeout);
      } catch (error) {
        setIdleTimeoutMinutes(60);
      } finally {
        setIsLoading(false);
      }
    };

    determineTimeout();
  }, [configApi, identityApi]);

  if (isLoading || idleTimeoutMinutes === undefined) {
    return null;
  }

  const promptBeforeIdleSeconds = configApi.getOptionalNumber('auth.autologout.promptBeforeIdleSeconds') ?? 60;
  const useWorkerTimers = configApi.getOptionalBoolean('auth.autologout.useWorkerTimers') ?? true;
  const logoutIfDisconnected = configApi.getOptionalBoolean('auth.autologout.logoutIfDisconnected') ?? true;

  return (
    <AutoLogout
      enabled
      idleTimeoutMinutes={idleTimeoutMinutes}
      promptBeforeIdleSeconds={promptBeforeIdleSeconds}
      useWorkerTimers={useWorkerTimers}
      logoutIfDisconnected={logoutIfDisconnected}
    />
  );
};
